import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Contact } from "@/api/queries";
import { useMe } from "@/auth/useMe";
import { Dialog } from "@/components/Dialog";
import { Button, Spinner } from "@/components/ui";
import { useSendingNumber } from "./queries";

/** `MIGUEL PONZZI` -> `Miguel Ponzzi`. Legacy rows shout; a greeting should not. */
export function readableName(name: string): string {
  const tidy = name.split(/\s+/).filter(Boolean).join(" ");
  if (tidy !== tidy.toUpperCase()) return tidy;
  return tidy
    .toLowerCase()
    .replace(
      /(^|\s)(\p{L})/gu,
      (_match, space: string, letter: string) => space + letter.toUpperCase(),
    );
}

/**
 * *Pedir WhatsApp* — the way to start on WhatsApp that needs no template.
 *
 * The agent sends the client a link, by email or a text message, that opens
 * WhatsApp on our number. When the client writes, the thread lands on their
 * contact with the consent recorded (.docs/06 §2.1, mechanism 1), and the
 * agent types freely for the next 24 hours.
 */
export function WhatsAppInvite({ contact }: { contact: Contact }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="btn btn-ghost px-2 py-1.5 text-sm"
        title={t("whatsapp.inviteHint")}
        onClick={() => setOpen(true)}
      >
        {t("whatsapp.invite")}
      </button>
      {/* Mounted only while open, so a card does not ask for the number until
          someone actually wants to send it. */}
      {open ? <InviteDialog contact={contact} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function InviteDialog({ contact, onClose }: { contact: Contact; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const number = useSendingNumber();
  const box = useRef<HTMLTextAreaElement>(null);
  const [edited, setEdited] = useState<string | null>(null);
  const [copy, setCopy] = useState<"idle" | "done" | "failed">("idle");

  const name = readableName(contact.name);
  const agent = readableName(me?.name || me?.email.split("@")[0] || "");
  // The greeting rides in the link, so the client's first message already says
  // who they are — useful when they write from a number that is not on file.
  const link = number.data
    ? `${number.data.wa_me}?text=${encodeURIComponent(t("whatsapp.inviteGreeting", { name }))}`
    : "";
  const message = edited ?? t("whatsapp.inviteMessage", { name, agent, link });
  const email = contact.emails[0]?.email;

  async function copyMessage() {
    try {
      await navigator.clipboard.writeText(message);
      setCopy("done");
    } catch {
      // No clipboard — an insecure origin, an old browser. Select the text so
      // Ctrl+C is all that is left to do.
      box.current?.select();
      setCopy("failed");
    }
  }

  const close = (
    <Button variant="ghost" className="ml-auto" onClick={onClose}>
      {t("common.close")}
    </Button>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      width={560}
      kicker={contact.name}
      title={t("whatsapp.inviteTitle")}
      footer={
        number.data ? (
          <>
            <Button variant="primary" onClick={() => void copyMessage()}>
              {copy === "done" ? t("whatsapp.copied") : t("whatsapp.copy")}
            </Button>
            {email ? (
              <a
                className="btn btn-secondary"
                href={`mailto:${email}?subject=${encodeURIComponent(
                  t("whatsapp.inviteSubject"),
                )}&body=${encodeURIComponent(message)}`}
              >
                {t("whatsapp.sendByEmail")}
              </a>
            ) : null}
            {close}
          </>
        ) : (
          close
        )
      }
    >
      <p className="mb-4 text-sm text-mute-600">{t("whatsapp.inviteLead")}</p>
      {number.isPending ? (
        <Spinner label={t("common.loading")} />
      ) : number.isError ? (
        <p role="alert" className="text-sm text-brand-700">
          {number.error.message}
        </p>
      ) : (
        <>
          <textarea
            ref={box}
            className="input min-h-32 w-full resize-y"
            aria-label={t("whatsapp.inviteMessageLabel")}
            value={message}
            onChange={(event) => setEdited(event.target.value)}
          />
          {copy === "failed" ? (
            <p className="mt-2 text-xs text-brand-700">{t("whatsapp.copyFailed")}</p>
          ) : null}
        </>
      )}
    </Dialog>
  );
}
